import { Link, useParams } from '@tanstack/react-router';
import type { ComponentProps, FC } from 'react';

import { DEFAULT_LOCALE, isAppLocale } from '~/utils/i18n';

type BaseLinkProps = ComponentProps<typeof Link>;

interface LocalizedLinkProps extends Omit<BaseLinkProps, 'to' | 'params'> {
  to: string;
  params?: Record<string, unknown>;
}

const ensureAbsolutePath = (path: string) =>
  path.startsWith('/') ? path : `/${path}`;

const ensureLocalePrefixedPath = (path: string, locale: string) => {
  const normalized = ensureAbsolutePath(path);

  // If it's already a locale-prefixed path, return as-is
  if (normalized.startsWith('/$locale')) {
    return normalized;
  }

  // Validate locale - if invalid, treat as default locale
  const validLocale = isAppLocale(locale) ? locale : DEFAULT_LOCALE;

  // For default locale, use root path without /$locale prefix
  if (validLocale === DEFAULT_LOCALE) {
    return normalized;
  }

  // For non-default locales, add /$locale prefix
  if (normalized === '/') {
    return '/$locale';
  }
  return `/$locale${normalized}`;
};

export const LocalizedLink: FC<LocalizedLinkProps> = ({
  to,
  params,
  ...rest
}) => {
  // Get current locale from route params, fallback to default
  const routeParams = useParams({ strict: false });
  const rawLocale = (routeParams as Record<string, string>)?.locale;

  // Validate locale - if invalid or not provided, use default
  const currentLocale = rawLocale && isAppLocale(rawLocale) ? rawLocale : DEFAULT_LOCALE;

  const target = ensureLocalePrefixedPath(to, currentLocale) as BaseLinkProps['to'];

  // For default locale, don't include locale in params
  // For non-default locales, include locale in params
  const mergedParams = currentLocale === DEFAULT_LOCALE
    ? params
    : {
        locale: currentLocale,
        ...params,
      };

  return <Link {...rest} params={mergedParams as unknown as BaseLinkProps['params']} to={target} />;
};


